/*
 Copyright 2024 Carnegie Mellon University. All Rights Reserved.
 Released under a MIT (SEI)-style license. See LICENSE.md in the
 project root for license information.
*/

import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  DataField,
  DataFieldType,
  MselItemStatus,
} from 'src/app/generated/blueprint.api';
import { EDITOR_CONFIG, VIEW_CONFIG } from 'src/app/utilities/editor-config';

@Component({
    selector: 'app-data-value',
    templateUrl: './data-value.component.html',
    styleUrls: ['./data-value.component.scss'],
    standalone: false
})
export class DataValueComponent {
  @Input() value: any;
  @Input() dataField: DataField;
  @Input() canEdit: boolean; // enables editing of values
  @Input() canApprove: boolean; // enables editing of status value
  @Input() isOwner: boolean; // enables editing of assigned unit value
  @Input() showValueOnly: boolean; // displays the value without a matFormField
  @Input() organizationOptions: any[] = [];
  @Input() teamOptions: any[] = [];
  @Input() unitOptions: any[] = [];
  @Input() cardOptions: any[] = [];
  @Input() moveOptions: any[] = [];
  @Input() gallerySourceTypeOptions: any[] = [];
  @Input() userOptions: any[] = [];
  @Output() valueChange = new EventEmitter<any>();
  editorConfig = EDITOR_CONFIG;
  viewConfig = VIEW_CONFIG;
  dataType: typeof DataFieldType = DataFieldType;
  itemStatus = [
    MselItemStatus.Pending,
    MselItemStatus.Entered,
    MselItemStatus.Approved,
    MselItemStatus.Complete,
    MselItemStatus.Deployed,
    MselItemStatus.Archived,
  ];

  // --- Competency chips + search list ---
  competencyFilter = '';

  get selectedCompetencies(): string[] {
    return this.value ? this.value.split(', ').filter(v => v) : [];
  }

  get filteredCompetencyOptions() {
    const options = this.dataField?.dataOptions || [];
    if (!this.competencyFilter) return options;
    const s = this.competencyFilter.toLowerCase();
    return options.filter(o =>
      o.optionName?.toLowerCase().includes(s) ||
      o.optionValue?.toLowerCase().includes(s) ||
      o.optionDescription?.toLowerCase().includes(s)
    );
  }

  displayCompetency = (val: string): string => {
    if (!val || !this.dataField?.dataOptions) return val || '';
    const opt = this.dataField.dataOptions.find(o => o.optionName === val);
    return opt ? opt.optionName : val;
  }

  getCompetencyTooltip = (val: string): string => {
    if (!val || !this.dataField?.dataOptions) return '';
    const opt = this.dataField.dataOptions.find(o => o.optionName === val);
    return opt?.optionValue || '';
  }

  isCompetencySelected(optionName: string): boolean {
    return this.selectedCompetencies.includes(optionName);
  }

  toggleCompetency(optionName: string) {
    const current = this.selectedCompetencies;
    const idx = current.indexOf(optionName);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(optionName);
    }
    this.value = current.join(', ');
    this.valueChangeHandler();
  }

  removeCompetency(id: string) {
    const current = this.selectedCompetencies.filter(v => v !== id);
    this.value = current.join(', ');
    this.valueChangeHandler();
  }

  valueChangeHandler() {
    this.valueChange.emit(this.value);
  }

  getValueArray(): any[] {
    return this.value ? this.value.split(', ') : [];
  }

  setDataValueArray(newValues: string[]) {
    if (newValues.includes('None')) {
      newValues = new Array();
    }
    this.value = newValues.join(', ');
    this.valueChangeHandler();
  }

  verifyNumber(value: string) {
    // remove non numeric characters, but allow '.' and '-'
    value = value.replace(/[^\d.-]/g, '');
  }

  getCardName(id: string): string {
    const card = this.cardOptions?.find((m) => m.id === id);
    return card ? card.name : '';
  }

  getMoveTitle(id: string): string {
    const move = this.moveOptions?.find((m) => m.id === id);
    return move ? move.title : '';
  }

  getUserName(id: string): string {
    const user = this.userOptions?.find((m) => m.id === id);
    return user ? user.name : '';
  }

  getUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }

  getTimezoneAbbr(): string {
    try {
      const date = new Date();
    const timeZone = this.getUserTimezone();
    const formatted = date.toLocaleTimeString('en-US', {
      timeZoneName: 'short',
      timeZone
    });
    const parts = formatted.split(' ');
    return parts[parts.length - 1] || 'UTC';
    } catch (error) {
      return 'UTC';
    }
  }
}
